// #/memories — month calendar with cover thumbnails, day view, and tabs
// "Buổi chụp" (the photo library) | "Đã chụp cùng" (people you shot with).

import { h } from '../dom';
import { api, previewUrl, type CalendarDay, type PersonTogether, type User } from '../../api/client';
import { avatar, empty, section, sheet, skeleton } from '../components';
import { shell } from '../shell';
import { queryParam } from '../router';
import { sessionCard, sessionList } from './library';

const TZ = 'Asia/Ho_Chi_Minh';
/** YYYY-MM-DD in Vietnam time. */
export const vnDay = (d = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const monthTitle = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return `Tháng ${m}, ${y}`;
};
const shiftMonth = (ym: string, by: number) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export function renderMemories(root: HTMLElement, _me: User) {
  const content = shell(root, { title: 'Kỷ niệm', back: '#/me' });
  const today = vnDay();
  const dayParam = queryParam('day');
  let month = queryParam('month') ?? (dayParam ? dayParam.slice(0, 7) : today.slice(0, 7));
  let tab: 'sessions' | 'people' = queryParam('tab') === 'people' ? 'people' : 'sessions';

  const syncUrl = () => history.replaceState(null, '', `#/memories?month=${month}&tab=${tab}`);

  // ---- calendar
  const cal = h('section.cal', { 'aria-label': 'Lịch kỷ niệm' });
  const paintCalendar = async () => {
    const title = h('h2.cal-title', {}, monthTitle(month));
    const prev = h('button.icon.small-icon', { 'aria-label': 'Tháng trước', onclick: () => { month = shiftMonth(month, -1); syncUrl(); void paintCalendar(); } }, '‹');
    const next = h('button.icon.small-icon', {
      'aria-label': 'Tháng sau', disabled: month >= today.slice(0, 7),
      onclick: () => { month = shiftMonth(month, 1); syncUrl(); void paintCalendar(); },
    }, '›');
    const grid = h('div.cal-grid', { role: 'grid' }, ...WEEKDAYS.map((w) => h('span.cal-wd', { role: 'columnheader' }, w)));
    const [y, m] = month.split('-').map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const lead = (first.getUTCDay() + 6) % 7; // Monday first
    const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const cells: HTMLElement[] = [];
    for (let i = 0; i < lead; i++) cells.push(h('span.cal-cell.pad'));
    for (let d = 1; d <= days; d++) {
      const key = `${month}-${String(d).padStart(2, '0')}`;
      cells.push(h('span.cal-cell', { class: key === today ? 'today' : key > today ? 'future' : '', 'data-day': key }, h('span.cal-num', {}, String(d))));
    }
    grid.append(...cells);
    cal.replaceChildren(h('div.cal-head', {}, prev, title, next), grid);
    cal.classList.add('loading');
    let list: CalendarDay[] = [];
    try {
      list = await api.memoryCalendar(month);
    } catch (e) {
      cal.append(h('p.tiny', {}, (e as Error).message));
    }
    cal.classList.remove('loading');
    let count = 0;
    for (const d of list) {
      const cell = grid.querySelector<HTMLElement>(`[data-day="${d.day}"]`);
      if (!cell || !d.sessions) continue;
      count++;
      const btn = h('button.cal-cell.has', {
        class: d.day === today ? 'today' : '',
        'aria-label': `${d.day}: ${d.sessions} buổi chụp`,
        onclick: () => openDay(d.day),
      }, h('span.cal-num', {}, String(Number(d.day.slice(8)))));
      if (d.cover) {
        const img = h('img.cal-cover', { alt: '', loading: 'lazy' });
        if (d.cover.url) img.src = d.cover.url;
        else if (d.cover.kind === 'PHOTO') void previewUrl({ id: d.cover.id }).then((u) => (img.src = u)).catch(() => img.remove());
        btn.prepend(img);
      }
      if (d.sessions > 1) btn.append(h('span.cal-dot', { 'aria-hidden': 'true' }, `×${d.sessions}`));
      cell.replaceWith(btn);
    }
    cal.append(h('p.cal-foot', {}, count ? `${count} ngày có kỷ niệm trong tháng này` : 'Tháng này chưa có buổi chụp nào. Rủ bạn chụp nhé ♡'));
  };

  // ---- tabs
  const body = h('div.memtab');
  const seg = h('div.seg', { role: 'tablist' });
  const paintTabs = () => {
    seg.replaceChildren(...([['sessions', '🎞 Buổi chụp'], ['people', '🫶 Đã chụp cùng']] as const).map(([t, label]) =>
      h('button.segbtn', {
        role: 'tab', 'aria-selected': String(tab === t), class: tab === t ? 'on' : '',
        onclick: () => { tab = t; syncUrl(); paintTabs(); },
      }, label)));
    body.replaceChildren(tab === 'sessions' ? sessionList() : peopleTogether());
  };

  content.append(cal, seg, body);
  paintTabs();
  void paintCalendar();
  if (dayParam) openDay(dayParam);
}

function openDay(day: string) {
  const box = h('div.dayview', {}, skeleton('post', 1));
  const title = new Date(`${day}T12:00:00`).toLocaleDateString('vi-VN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  sheet(title.charAt(0).toUpperCase() + title.slice(1), box);
  api.memoryDay(day).then((d) => {
    if (!d.sessions.length) return box.replaceChildren(empty('🌙', 'Ngày này chưa có buổi chụp nào.'));
    box.replaceChildren(...d.sessions.map((s) => sessionCard(s)));
  }).catch((e) => box.replaceChildren(h('p.tiny', {}, (e as Error).message)));
}

const dateFmt = new Intl.DateTimeFormat('vi-VN', { day: 'numeric', month: 'numeric', year: 'numeric' });

/** "Đã chụp cùng": everyone you shot a booth session with, most recent first. */
export function peopleTogether() {
  return section(() => api.peopleTogether(), (list) => {
    if (!list.length)
      return empty('🫶', 'Chụp booth cùng bạn bè, mỗi người sẽ có một ô ở đây.', h('a.primary.btn', { href: '#/rooms' }, '🎞 Rủ bạn chụp'), 'Chưa chụp cùng ai');
    return h('div.pgrid', {}, ...list.map(personCard));
  }, () => skeleton('grid', 6));
}

function personCard(p: PersonTogether) {
  const cover = h('span.pcover', { 'aria-hidden': 'true' });
  if (p.coverPhotoId) {
    const img = h('img', { alt: '', loading: 'lazy', decoding: 'async' });
    void previewUrl({ id: p.coverPhotoId }).then((u) => (img.src = u)).catch(() => img.remove());
    cover.append(img);
  }
  return h('a.pcard', { href: `#/u/${p.user.id}`, 'aria-label': `${p.user.displayName}, ${p.sessionsTogether} buổi chụp chung` },
    cover,
    h('span.pcard-av', {}, avatar(p.user, 48)),
    h('b.pcard-name', {}, p.user.displayName),
    h('span.pcard-count', {}, `🎞 ${p.sessionsTogether} buổi`),
    h('small', {}, `Gần nhất ${dateFmt.format(new Date(p.lastAt))}`),
    h('small.pcard-first', {}, `Lần đầu ${dateFmt.format(new Date(p.firstAt))}`));
}
